import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, TextField, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Paper, Divider,
  FormControl, InputLabel, Select, MenuItem, Chip, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { Download, TrendingUp, Inventory, People, BusinessCenter, Store, ShoppingCart, Assessment, ExpandMore } from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';
import { reportsAPI, usersAPI, productsAPI, inventoryAPI } from '../services/api';
import { toast } from 'react-toastify';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const COLORS = ['#1a237e', '#3949ab', '#7986cb', '#c5cae9', '#e53935'];

const ReportsPage = () => {
  const [category, setCategory] = useState('sales');
  const [subTab, setSubTab] = useState(0);
  const [salesData, setSalesData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [staffData, setStaffData] = useState([]);
  const [plData, setPlData] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [aggregation, setAggregation] = useState('monthly');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  
  // Filter lists
  const [usersList, setUsersList] = useState([]);
  const [warehousesList, setWarehousesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  
  // New report data states
  const [salesByProductType, setSalesByProductType] = useState([]);
  const [salesByUser, setSalesByUser] = useState([]);
  const [productExpenses, setProductExpenses] = useState([]);
  const [productRevenue, setProductRevenue] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [mostSoldProducts, setMostSoldProducts] = useState([]);
  const [topStores, setTopStores] = useState([]);
  const [lowestStores, setLowestStores] = useState([]);
  const [warehousePL, setWarehousePL] = useState([]);
  const [productSalesDetails, setProductSalesDetails] = useState([]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getSales({ 
        date_from: dateFrom, 
        date_to: dateTo, 
        group_by: aggregation === 'daily' ? 'day' : aggregation === 'weekly' ? 'week' : aggregation === 'quarterly' ? 'quarter' : aggregation,
        salesperson_id: selectedUser || undefined,
        product_id: selectedProduct || undefined,
        warehouse_id: selectedWarehouse || undefined
      });
      setSalesData(data.data);
    } catch { toast.error('Failed to load sales report'); }
    setLoading(false);
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getInventory();
      setInventoryData(data.data);
    } catch { toast.error('Failed to load inventory report'); }
    setLoading(false);
  };

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getStaffPerformance({ date_from: dateFrom, date_to: dateTo });
      setStaffData(data.data || []);
    } catch { toast.error('Failed to load staff report'); }
    setLoading(false);
  };

  const fetchPL = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getProfitLoss({ date_from: dateFrom, date_to: dateTo, warehouse_id: selectedWarehouse || undefined });
      setPlData(data.data);
    } catch { toast.error('Failed to load P&L'); }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getCustomers();
      setCustomerData(data.data || []);
    } catch { toast.error('Failed to load customer report'); }
    setLoading(false);
  };

  // New report fetch functions
  const fetchSalesByProductType = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getSalesByProductType({ 
        date_from: dateFrom, 
        date_to: dateTo, 
        aggregation,
        warehouse_id: selectedWarehouse || undefined
      });
      setSalesByProductType(data.data || []);
    } catch { toast.error('Failed to load sales by product type'); }
    setLoading(false);
  };

  const fetchSalesByUser = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getSalesByUser({ 
        date_from: dateFrom, 
        date_to: dateTo,
        aggregation,
        user_id: selectedUser || undefined,
        warehouse_id: selectedWarehouse || undefined
      });
      setSalesByUser(data.data || []);
    } catch { toast.error('Failed to load sales by user'); }
    setLoading(false);
  };

  const fetchProductExpenses = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getProductExpenses({ 
        date_from: dateFrom, 
        date_to: dateTo, 
        product_ids: selectedProduct || undefined,
        warehouse_id: selectedWarehouse || undefined
      });
      setProductExpenses(data.data || []);
    } catch { toast.error('Failed to load product expenses'); }
    setLoading(false);
  };

  const fetchProductRevenue = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getProductRevenue({ 
        date_from: dateFrom, 
        date_to: dateTo, 
        product_ids: selectedProduct || undefined,
        warehouse_id: selectedWarehouse || undefined
      });
      setProductRevenue(data.data || []);
    } catch { toast.error('Failed to load product revenue'); }
    setLoading(false);
  };

  const fetchTopCustomers = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getTopCustomers({ 
        date_from: dateFrom, 
        date_to: dateTo,
        limit: 20,
        warehouse_id: selectedWarehouse || undefined
      });
      setTopCustomers(data.data || []);
    } catch { toast.error('Failed to load top customers'); }
    setLoading(false);
  };

  const fetchMostSoldProducts = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getMostSoldProducts({ 
        date_from: dateFrom, 
        date_to: dateTo,
        warehouse_id: selectedWarehouse || undefined,
        limit: 20
      });
      setMostSoldProducts(data.data || []);
    } catch { toast.error('Failed to load most sold products'); }
    setLoading(false);
  };

  const fetchTopStores = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getTopStores({ 
        date_from: dateFrom, 
        date_to: dateTo,
        limit: 10
      });
      setTopStores(data.data || []);
    } catch { toast.error('Failed to load top stores'); }
    setLoading(false);
  };

  const fetchLowestStores = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getLowestStores({ 
        date_from: dateFrom, 
        date_to: dateTo,
        limit: 10
      });
      setLowestStores(data.data || []);
    } catch { toast.error('Failed to load lowest stores'); }
    setLoading(false);
  };

  // Fetch filter lists
  const fetchFilterLists = async () => {
    try {
      const [usersRes, productsRes, warehousesRes] = await Promise.allSettled([
        usersAPI.getAll({ limit: 100 }),
        productsAPI.getAll({ limit: 100 }),
        inventoryAPI.getWarehouses()
      ]);
      setUsersList(usersRes.status === 'fulfilled' ? usersRes.value.data.data || [] : []);
      setProductsList(productsRes.status === 'fulfilled' ? productsRes.value.data.data || [] : []);
      setWarehousesList(warehousesRes.status === 'fulfilled' ? warehousesRes.value.data.data || [] : []);
    } catch (err) {
      console.error('Failed to load filter lists:', err);
    }
  };

  // New report functions
  const fetchWarehousePL = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getProfitLoss({ date_from: dateFrom, date_to: dateTo, warehouse_id: selectedWarehouse || undefined });
      setWarehousePL([data.data]);
    } catch { toast.error('Failed to load warehouse P&L'); }
    setLoading(false);
  };

  const fetchProductSalesDetails = async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.getProductRevenue({ 
        date_from: dateFrom, 
        date_to: dateTo,
        product_ids: selectedProduct || undefined,
        warehouse_id: selectedWarehouse || undefined
      });
      setProductSalesDetails(data.data || []);
    } catch { toast.error('Failed to load product sales details'); }
    setLoading(false);
  };

  useEffect(() => {
    fetchFilterLists();
  }, []);

  useEffect(() => {
    setSubTab(0);
  }, [category]);

  useEffect(() => {
    if (category === 'sales') {
      if (subTab === 0) fetchSales();
      else if (subTab === 1) fetchSalesByProductType();
      else if (subTab === 2) fetchSalesByUser();
    } else if (category === 'products') {
      if (subTab === 0) fetchProductExpenses();
      else if (subTab === 1) fetchProductRevenue();
      else if (subTab === 2) fetchMostSoldProducts();
      else if (subTab === 3) fetchProductSalesDetails();
    } else if (category === 'stores') {
      if (subTab === 0) fetchTopStores();
      else if (subTab === 1) fetchLowestStores();
      else if (subTab === 2) fetchWarehousePL();
    } else if (category === 'customers') {
      if (subTab === 0) fetchTopCustomers();
    } else if (category === 'overview') {
      if (subTab === 0) fetchInventory();
      else if (subTab === 1) fetchStaff();
      else if (subTab === 2) fetchPL();
    }
  }, [category, subTab, dateFrom, dateTo, aggregation, selectedProduct, selectedWarehouse, selectedUser]);

  const exportCSV = (data, filename) => {
    if (!data?.length) return;
    const keys = Object.keys(data[0]);
    const csv = [keys.join(','), ...data.map(r => keys.map(k => `"${r[k] ?? ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${filename}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
          Reports & Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and analyze your business performance with detailed reports
        </Typography>
      </Box>

      {/* Filters Section */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Report Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField 
                label="From" 
                type="date" 
                size="small" 
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateFrom} 
                onChange={(e) => setDateFrom(e.target.value)} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField 
                label="To" 
                type="date" 
                size="small" 
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateTo} 
                onChange={(e) => setDateTo(e.target.value)} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Aggregation</InputLabel>
                <Select value={aggregation} label="Aggregation" onChange={(e) => setAggregation(e.target.value)}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>User</InputLabel>
                <Select value={selectedUser} label="User" onChange={(e) => setSelectedUser(e.target.value)}>
                  <MenuItem value="">All Users</MenuItem>
                  {usersList.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Warehouse</InputLabel>
                <Select value={selectedWarehouse} label="Warehouse" onChange={(e) => setSelectedWarehouse(e.target.value)}>
                  <MenuItem value="">All Warehouses</MenuItem>
                  {warehousesList.map(w => (
                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Product</InputLabel>
                <Select value={selectedProduct} label="Product" onChange={(e) => setSelectedProduct(e.target.value)}>
                  <MenuItem value="">All Products</MenuItem>
                  {productsList.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Category Tabs */}
      <Tabs value={category} onChange={(_, v) => setCategory(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab value="sales" label="Sales" />
        <Tab value="products" label="Products" />
        <Tab value="stores" label="Stores/Warehouses" />
        <Tab value="customers" label="Customers" />
        <Tab value="overview" label="Overview" />
      </Tabs>

      {/* Sales Category */}
      {category === 'sales' && (
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
          <Tab label="General Sales" />
          <Tab label="By Product Type" />
          <Tab label="By User" />
        </Tabs>
      )}

      {/* Products Category */}
      {category === 'products' && (
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
          <Tab label="Product Expenses" />
          <Tab label="Product Revenue" />
          <Tab label="Most Sold" />
          <Tab label="Product Details" />
        </Tabs>
      )}

      {/* Stores Category */}
      {category === 'stores' && (
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
          <Tab label="Top Performing" />
          <Tab label="Lowest Performing" />
          <Tab label="Warehouse P&L" />
        </Tabs>
      )}

      {/* Customers Category */}
      {category === 'customers' && (
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
          <Tab label="Top Customers" />
        </Tabs>
      )}

      {/* Overview Category */}
      {category === 'overview' && (
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
          <Tab label="Inventory" />
          <Tab label="Staff Performance" />
          <Tab label="P&L" />
        </Tabs>
      )}

      {/* Sales - General Sales */}
      {category === 'sales' && subTab === 0 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <>
              {salesData?.summary && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {[
                    { label: 'Total Revenue', value: fmt(salesData.summary.total_revenue), color: 'primary' },
                    { label: 'Total Orders', value: salesData.summary.total_orders, color: 'info' },
                    { label: 'Avg Order Value', value: fmt(salesData.summary.avg_order_value), color: 'success' },
                    { label: 'Total Discounts', value: fmt(salesData.summary.total_discounts), color: 'warning' },
                  ].map(s => (
                    <Grid item xs={6} md={3} key={s.label}>
                      <Card><CardContent>
                        <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                        <Typography variant="h5" fontWeight={700} color={`${s.color}.main`}>{s.value}</Typography>
                      </CardContent></Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>Monthly Revenue</Typography>
                    <Button size="small" startIcon={<Download />} onClick={() => exportCSV(salesData?.chart_data, 'sales-report')}>Export CSV</Button>
                  </Box>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={salesData?.chart_data || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period_label" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [fmt(v)]} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#1a237e" name="Revenue" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      )}

      {/* Sales - By Product Type */}
      {category === 'sales' && subTab === 1 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Sales by Product Type</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(salesByProductType, 'sales-by-product-type')}>Export</Button>
              </Box>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={salesByProductType}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period_label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [fmt(v)]} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#1a237e" name="Revenue" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Divider sx={{ my: 2 }} />
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Product Type</TableCell><TableCell>Period</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Units Sold</TableCell><TableCell align="right">Revenue</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {salesByProductType.slice(0, 50).map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell fontWeight={600}>{row.product_type || 'N/A'}</TableCell>
                      <TableCell>{row.period_label}</TableCell>
                      <TableCell align="right">{row.orders}</TableCell>
                      <TableCell align="right">{row.units_sold}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmt(row.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Sales - By User */}
      {category === 'sales' && subTab === 2 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Sales by User</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(salesByUser, 'sales-by-user')}>Export</Button>
              </Box>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={salesByUser}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period_label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [fmt(v)]} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#3949ab" name="Revenue" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Divider sx={{ my: 2 }} />
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>User</TableCell><TableCell>Period</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Revenue</TableCell><TableCell align="right">Avg Order</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {salesByUser.slice(0, 50).map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell fontWeight={600}>{row.user_name}</TableCell>
                      <TableCell>{row.period_label}</TableCell>
                      <TableCell align="right">{row.orders}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmt(row.revenue)}</TableCell>
                      <TableCell align="right">{fmt(row.avg_order_value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Products - Expenses */}
      {category === 'products' && subTab === 0 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Product Expenses</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(productExpenses, 'product-expenses')}>Export</Button>
              </Box>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Product</TableCell><TableCell>SKU</TableCell>
                  <TableCell align="right">Cost Price</TableCell>
                  <TableCell align="right">Total Quantity</TableCell><TableCell align="right">Total Cost</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {productExpenses.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell fontWeight={600}>{row.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.sku}</TableCell>
                      <TableCell align="right">{fmt(row.cost_price)}</TableCell>
                      <TableCell align="right">{row.total_quantity}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>{fmt(row.total_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Products - Revenue */}
      {category === 'products' && subTab === 1 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Product Revenue & Profit</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(productRevenue, 'product-revenue')}>Export</Button>
              </Box>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Product</TableCell><TableCell>SKU</TableCell>
                  <TableCell align="right">Selling Price</TableCell>
                  <TableCell align="right">Total Quantity</TableCell>
                  <TableCell align="right">Total Revenue</TableCell>
                  <TableCell align="right">Total Cost</TableCell><TableCell align="right">Profit</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {productRevenue.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell fontWeight={600}>{row.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.sku}</TableCell>
                      <TableCell align="right">{fmt(row.selling_price)}</TableCell>
                      <TableCell align="right">{row.total_quantity}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmt(row.total_revenue)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{fmt(row.total_cost)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: row.profit >= 0 ? 'success.main' : 'error.main' }}>{fmt(row.profit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Products - Most Sold */}
      {category === 'products' && subTab === 2 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Most Sold Products</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(mostSoldProducts, 'most-sold-products')}>Export</Button>
              </Box>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={mostSoldProducts} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => v} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [v, 'Units']} />
                  <Legend />
                  <Bar dataKey="total_quantity" fill="#7986cb" name="Units Sold" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Divider sx={{ my: 2 }} />
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Product</TableCell><TableCell>SKU</TableCell><TableCell>Brand</TableCell>
                  <TableCell align="right">Units Sold</TableCell>
                  <TableCell align="right">Total Revenue</TableCell><TableCell align="right">Order Count</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {mostSoldProducts.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell fontWeight={600}>{row.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.sku}</TableCell>
                      <TableCell>{row.brand || 'N/A'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{row.total_quantity}</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>{fmt(row.total_revenue)}</TableCell>
                      <TableCell align="right">{row.order_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Products - Product Details */}
      {category === 'products' && subTab === 3 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Product Sales Details</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(productSalesDetails, 'product-sales-details')}>Export</Button>
              </Box>
              {selectedProduct ? (
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Product</TableCell><TableCell>SKU</TableCell>
                    <TableCell align="right">Selling Price</TableCell>
                    <TableCell align="right">Total Quantity</TableCell>
                    <TableCell align="right">Total Revenue</TableCell>
                    <TableCell align="right">Total Cost</TableCell><TableCell align="right">Profit</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {productSalesDetails.map((row, i) => (
                      <TableRow key={i} hover>
                        <TableCell fontWeight={600}>{row.name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.sku}</TableCell>
                        <TableCell align="right">{fmt(row.selling_price)}</TableCell>
                        <TableCell align="right">{row.total_quantity}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmt(row.total_revenue)}</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>{fmt(row.total_cost)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: row.profit >= 0 ? 'success.main' : 'error.main' }}>{fmt(row.profit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">Select a product to view detailed sales information</Typography>
              )}
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Stores - Top Performing */}
      {category === 'stores' && subTab === 0 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Top Performing Stores</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(topStores, 'top-stores')}>Export</Button>
              </Box>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={topStores}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="store_name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [fmt(v)]} />
                  <Legend />
                  <Bar dataKey="total_revenue" fill="#1a237e" name="Revenue" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Divider sx={{ my: 2 }} />
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Store</TableCell><TableCell>Location</TableCell>
                  <TableCell align="right">Total Orders</TableCell>
                  <TableCell align="right">Total Revenue</TableCell>
                  <TableCell align="right">Avg Order</TableCell><TableCell align="right">Unique Customers</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {topStores.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell fontWeight={600}>{row.store_name}</TableCell>
                      <TableCell>{row.location}</TableCell>
                      <TableCell align="right">{row.total_orders}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmt(row.total_revenue)}</TableCell>
                      <TableCell align="right">{fmt(row.avg_order_value)}</TableCell>
                      <TableCell align="right">{row.unique_customers}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Stores - Lowest Performing */}
      {category === 'stores' && subTab === 1 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Lowest Performing Stores</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(lowestStores, 'lowest-stores')}>Export</Button>
              </Box>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Store</TableCell><TableCell>Location</TableCell>
                  <TableCell align="right">Total Orders</TableCell>
                  <TableCell align="right">Total Revenue</TableCell>
                  <TableCell align="right">Avg Order</TableCell><TableCell align="right">Unique Customers</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {lowestStores.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell fontWeight={600}>{row.store_name}</TableCell>
                      <TableCell>{row.location}</TableCell>
                      <TableCell align="right">{row.total_orders}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>{fmt(row.total_revenue)}</TableCell>
                      <TableCell align="right">{fmt(row.avg_order_value)}</TableCell>
                      <TableCell align="right">{row.unique_customers}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Stores - Warehouse P&L */}
      {category === 'stores' && subTab === 2 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Warehouse Profit & Loss</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(warehousePL, 'warehouse-pl')}>Export</Button>
              </Box>
              {!selectedWarehouse && <Typography color="text.secondary">Select a warehouse above to view its filtered P&amp;L.</Typography>}
              {selectedWarehouse && warehousePL[0] && (
                <Grid container spacing={3}>
                  {[
                    { label: 'Total Revenue', value: fmt(warehousePL[0].revenue?.total_revenue), color: 'success' },
                    { label: 'Cost of Goods', value: fmt(warehousePL[0].cost_of_goods), color: 'error' },
                    { label: 'Gross Profit', value: fmt(warehousePL[0].gross_profit), color: 'primary' },
                    { label: 'Gross Margin', value: `${warehousePL[0].gross_margin_percent}%`, color: 'info' },
                  ].map(item => (
                    <Grid item xs={6} md={3} key={item.label}>
                      <Card><CardContent>
                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                        <Typography variant="h5" fontWeight={700} color={`${item.color}.main`}>{item.value}</Typography>
                      </CardContent></Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Customers - Top Customers */}
      {category === 'customers' && subTab === 0 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card><CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Top Buying Customers</Typography>
                <Button size="small" startIcon={<Download />} onClick={() => exportCSV(topCustomers, 'top-customers')}>Export</Button>
              </Box>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Phone</TableCell>
                  <TableCell align="right">Orders</TableCell><TableCell align="right">Total Spent</TableCell>
                  <TableCell align="right">Avg Order</TableCell><TableCell>Last Purchase</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {topCustomers.map((c, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.customer_code}</TableCell>
                      <TableCell fontWeight={600}>{c.name}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.phone}</TableCell>
                      <TableCell align="right">{c.total_orders}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmt(c.total_spent)}</TableCell>
                      <TableCell align="right">{fmt(c.avg_order_value)}</TableCell>
                      <TableCell>{c.last_purchase_date ? new Date(c.last_purchase_date).toLocaleDateString() : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </Box>
      )}

      {/* Overview - Inventory */}
      {category === 'overview' && subTab === 0 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <>
              {inventoryData?.summary && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}><Card><CardContent>
                    <Typography variant="body2" color="text.secondary">Total Products</Typography>
                    <Typography variant="h5" fontWeight={700}>{inventoryData.summary.total_products}</Typography>
                  </CardContent></Card></Grid>
                  <Grid item xs={6} md={3}><Card><CardContent>
                    <Typography variant="body2" color="text.secondary">Stock Value</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">{fmt(inventoryData.summary.total_stock_value)}</Typography>
                  </CardContent></Card></Grid>
                  <Grid item xs={6} md={3}><Card><CardContent>
                    <Typography variant="body2" color="text.secondary">Potential Revenue</Typography>
                    <Typography variant="h5" fontWeight={700} color="success.main">{fmt(inventoryData.summary.total_potential_revenue)}</Typography>
                  </CardContent></Card></Grid>
                  <Grid item xs={6} md={3}><Card><CardContent>
                    <Typography variant="body2" color="text.secondary">Low Stock Items</Typography>
                    <Typography variant="h5" fontWeight={700} color="error.main">{inventoryData.summary.low_stock_items}</Typography>
                  </CardContent></Card></Grid>
                </Grid>
              )}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>Inventory Valuation</Typography>
                    <Button size="small" startIcon={<Download />} onClick={() => exportCSV(inventoryData?.items, 'inventory-report')}>Export CSV</Button>
                  </Box>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Vehicle</TableCell><TableCell>SKU</TableCell>
                      <TableCell align="right">Stock</TableCell><TableCell align="right">Cost Price</TableCell>
                      <TableCell align="right">Sell Price</TableCell><TableCell align="right">Stock Value</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {(inventoryData?.items || []).slice(0, 50).map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell fontWeight={600}>{item.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.sku}</TableCell>
                          <TableCell align="right">{item.stock_quantity}</TableCell>
                          <TableCell align="right">{fmt(item.cost_price)}</TableCell>
                          <TableCell align="right">{fmt(item.selling_price)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(item.stock_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      )}

      {/* Overview - Staff Performance */}
      {category === 'overview' && subTab === 1 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700}>Staff Performance</Typography>
                  <Button size="small" startIcon={<Download />} onClick={() => exportCSV(staffData, 'staff-performance')}>Export</Button>
                </Box>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Name</TableCell><TableCell>Email</TableCell>
                    <TableCell align="right">Orders</TableCell><TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Avg Order</TableCell><TableCell align="right">Commission</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {staffData.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell fontWeight={600}>{s.name}</TableCell>
                        <TableCell>{s.email}</TableCell>
                        <TableCell align="right">{s.total_orders}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmt(s.total_revenue)}</TableCell>
                        <TableCell align="right">{fmt(s.avg_order_value)}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(s.total_commission)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Overview - P&L */}
      {category === 'overview' && subTab === 2 && (
        <Box sx={{ pt: 2 }}>
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : plData && (
            <Grid container spacing={3}>
              {[
                { label: 'Total Revenue', value: fmt(plData.revenue?.total_revenue), color: 'success' },
                { label: 'Cost of Goods', value: fmt(plData.cost_of_goods), color: 'error' },
                { label: 'Gross Profit', value: fmt(plData.gross_profit), color: 'primary' },
                { label: 'Gross Margin', value: `${plData.gross_margin_percent}%`, color: 'info' },
              ].map(item => (
                <Grid item xs={6} md={3} key={item.label}>
                  <Card><CardContent>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="h4" fontWeight={700} color={`${item.color}.main`}>{item.value}</Typography>
                  </CardContent></Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReportsPage;
